<template>
  <div class="listing-title px-3">
    <p class="text-sm-left grey--text text--darken-2 mb-2" v-text="location" />
    <div
      v-if="fullStarsCount > 3"
      class="d-flex justify-start align-center justify-md-start mb-4 mt-6"
    >
      <v-icon
        :small="isMobileOrIPad"
        v-for="s in fullStarsCount"
        :key="s"
        class="mr-2"
        color="yellow darken-2"
        >fas fa-star</v-icon
      >
      <v-icon :small="isMobileOrIPad" v-if="hasHalfStar" color="yellow darken-2"
        >fas fa-star-half</v-icon
      >
      <span
        v-if="reviews?.count > 1"
        class="grey--text text-body-2 text-md-body-2"
        >({{ reviews.count }}) reviews</span
      >
    </div>
    <h1
      class="text-h5 text-sm-h4 font-weight-semibold mb-2"
      v-text="listing.title"
    />
    <div class="d-flex justify-start align-center">
      <div class="d-flex text-body-2 mt-0" v-if="listingInfo">
        <span>{{ listingInfo?.guests?.count }} {{ $t("guests") }}</span>
        <span class="mx-2">|</span>
        <span>{{ listingInfo?.beds?.count }} {{ $t("bedrooms") }}</span>
        <span class="mx-2">|</span>
        <span>{{ listingInfo?.bathrooms?.count }} {{ $t("bathrooms") }}</span>
      </div>
      <!-- <listing-amenities v-if="false" :items="listing.amenities.brief" mini /> -->
      <v-spacer class="d-none d-sm-block" />
      <div
        v-if="!isAgentReserveSite"
        :class="[
          'd-sm-block',
          {
            'p-absolute right-0': isMobile,
          },
        ]"
      >
        <v-btn
          v-if="isMobile"
          @click="share"
          class="text-capitalize text--secondary text-caption"
          text
        >
          <v-icon class="mr-2" x-small>$share</v-icon>
          {{ $t("share") }}
        </v-btn>
        <v-menu v-else left>
          <template #activator="{ on }">
            <v-btn class="text-capitalize" v-on="on" text>
              <v-icon class="mr-2" small>$share</v-icon>
              Share
            </v-btn>
          </template>
          <v-list>
            <v-btn @click="webShare('whatsapp')" class="my-2" text>
              <div>
                <v-icon class="mx-2 mb-1">mdi-whatsapp</v-icon>
                <div class="text-caption">Whatsapp</div>
              </div>
            </v-btn>
            <v-btn @click="webShare('facebook')" text>
              <div>
                <v-icon
                  href="https://www.facebook.com/sharer/sharer.php?u=URL"
                  class="mx-2 mb-1"
                  >mdi-facebook</v-icon
                >
                <div class="text-caption">Facebook</div>
              </div>
            </v-btn>
            <v-btn @click="webShare('email')" text>
              <div>
                <v-icon class="mx-2 mb-1">mdi-email</v-icon>
                <div class="text-caption">email</div>
              </div>
            </v-btn>
            <v-btn @click="webShare('clipboard')" text>
              <div>
                <v-icon class="mx-2 mb-1">mdi-link</v-icon>
                <div class="text-caption">link</div>
              </div>
            </v-btn>
          </v-list>
        </v-menu>
        <!-- <v-btn class="text-capitalize" text>
          <v-icon class="mr-2" small>far fa-heart</v-icon>
          Save
        </v-btn> -->
      </div>
    </div>
    <div
      v-if="listing.amenities.includes('Shelter')"
      class="d-flex justify-start align-center mt-2"
    >
      <span
        class="orange white--text text-caption rounded-lg d-flex align-center shelter-badge"
      >
        <v-icon x-small class="mr-1" color="white">mdi-shield-home</v-icon>
        {{ $t("Shelter") }}
      </span>
    </div>
  </div>
</template>

<script>
import DeviceMixin from "@/mixins/device-mixin";
import Toaster from "@/utils/toaster";
import { mapState } from "vuex";

export default {
  name: "listing-title",
  mixins: [DeviceMixin],
  props: {
    listing: Object,
    reviews: Object,
  },
  watch: {
    settings: {
      handler() {
        if (this.settings?.site_title) {
          document.title = `${this.settings?.site_title || "Booking"} | ${
            this.listing.title
          }`;
          this.setDocumentMetaDescription();
        }
      },
    },
  },
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),
    isAgentReserveSite() {
      return this.settings.site_type === "agent_reserve";
    },
    listingInfo() {
      const beds = { type: "bedrooms", count: this.listing.beds };
      const bathrooms = { type: "bathrooms", count: this.listing.baths };
      const guests = { type: "guests", count: this.listing.accommodates };
      return { beds, bathrooms, guests };
    },
    location() {
      if (
        this.settings?.custom_settings?.show_full_address &&
        this.listing.address
      ) {
        return this.listing.address;
      }
      const { city_name, state_name } = this.listing;
      return `${city_name} ${state_name ? `, ${state_name}` : ""}`;
    },
    reviewScore() {
      return this.listing?.airbnb_score || this.reviews?.avg_score;
    },
    fullStarsCount() {
      return Math.floor(this.reviewScore);
    },
    hasHalfStar() {
      return this.reviewScore % 1 !== 0;
    },
  },
  methods: {
    setDocumentMetaDescription() {
      let description =
        this.listing?.marketing_content?.summary || this.listing?.name;
      if (description && description.length > 150) {
        description = description.slice(0, 147) + "...";
      }
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", description);
    },
    webShare(source) {
      let shareUrl = "";
      const currentUrl = window.location.href;
      if (source === "whatsapp")
        shareUrl = `https://wa.me/?text=Check out this listing ${currentUrl}`;

      if (source === "facebook")
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`;
      if (source === "email")
        shareUrl = `mailto:?subject=Check out this listing&body=${currentUrl}`;
      if (source === "clipboard") {
        navigator.clipboard.writeText(currentUrl);
        Toaster.show([{ type: "success", text: "Link copied to Clipboard!" }]);
        return;
      }

      window.open(shareUrl, "_blank");
    },
    share() {
      if (navigator.share) {
        const currentUrl = window.location.href;

        navigator.share({
          title: "Check out this listing!",
          url: currentUrl,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.v-menu__content {
  border: solid 1px #e9e9e9 !important;
  border-radius: 20px;
  box-shadow: none !important;
}
.right-0 {
  right: 0 !important;
}
.shelter-badge {
  padding: 1px 5px;
}
</style>
